import { CardConditionalHighlight, CardTitle } from "../common";
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  faCheck,
  faClock,
  faExclamation,
  faStopwatch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faCog, faPlay } from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cronParser from "cron-parser";
import { normalizeTimestamp } from "../../helpers/utils";

const CronCard = React.forwardRef(
  (
    {
      card,
      topic,
      messages,
      w,
      h,
      displayVariant,
      displayParameters,
      pipeline = false,
    },
    ref
  ) => {
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [nextRun, setNextRun] = useState(null);
    const [countdown, setCountdown] = useState("");

    const safeMessages = messages && messages.length > 0 ? messages : [];
    const effectiveLastMessage = useMemo(
      () => safeMessages[0] || null,
      [safeMessages]
    );

    const calculateNextRun = () => {
      if (!card.params?.schedule) {
        setNextRun(null);
        return;
      }
      try {
        const options = {
          currentDate: effectiveLastMessage
            ? new Date(normalizeTimestamp(effectiveLastMessage.timestamp))
            : new Date(),
        };
        const interval = cronParser.parseExpression(
          card.params.schedule,
          options
        );
        const next = interval.next().toDate();
        setNextRun(
          next.getTime() > Date.now() ? next : new Date(Date.now() + 60000)
        );
      } catch (err) {
        console.error("Error parsing cron expression:", err);
        setNextRun(null);
      }
    };

    useEffect(() => {
      const timerId = setInterval(() => setCurrentTime(Date.now()), 1000);
      return () => clearInterval(timerId);
    }, []);

    useEffect(() => {
      calculateNextRun();
    }, [card.params?.schedule, effectiveLastMessage]);

    useEffect(() => {
      if (!nextRun) {
        setCountdown("Invalid cron schedule.");
        return;
      }
      const diffInMs = nextRun.getTime() - currentTime;
      if (diffInMs <= 0 && effectiveLastMessage) {
        calculateNextRun();
        setCountdown("Next run is now.");
      } else {
        const totalSeconds = Math.floor(diffInMs / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        setCountdown(
          `${days ? days + "d " : ""}${hours ? hours + "h " : ""}${
            minutes ? minutes + "m " : ""
          }${seconds}s`
        );
      }
    }, [currentTime, nextRun, effectiveLastMessage]);

    const action = (setSelectedCard) => setSelectedCard(card);
    useImperativeHandle(ref, () => ({ action }));
    const [showCondition, setShowCondition] = useState(false);

    const timestamp = effectiveLastMessage?.timestamp || null;
    let timeText;
    let totalMinutes = 999999;
    if (!timestamp) {
      timeText = "Trigger has not run yet";
    } else {
      const runTime = new Date(normalizeTimestamp(timestamp));
      const timeDifference = currentTime - runTime.getTime();
      totalMinutes = Math.floor(timeDifference / (1000 * 60));
      const hrs = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      timeText =
        totalMinutes < 1
          ? "less than 1 minute ago"
          : `${hrs ? `${hrs}h ` : ""}${mins}m ago`;
    }

    const sizeKey = `${w}x${h}`;
    const statusConfig = getCronStatusConfig(totalMinutes);

    const renderContent = () => {
      switch (sizeKey) {
        case "1x1":
        case "2x1":
          return (
            <div className="flex flex-col justify-around w-full h-full">
              <CardTitle title={topic.name} />
              <div className="flex justify-around">
                <FontAwesomeIcon
                  icon={faStopwatch}
                  className={`${statusConfig.iconColor} rounded-full`}
                  size="2x"
                />
                <p className="text-sm text-delft-blue my-auto font-sans">
                  {countdown}
                </p>
              </div>
            </div>
          );
        case "1x2":
          return (
            <div className="flex flex-col items-center justify-center w-full h-full p-4 rounded-lg">
              <FontAwesomeIcon
                icon={faClock}
                className={`text-gray-600 text-3xl mb-2 ${statusConfig.iconColor}`}
              />
              <p className="text-sm font-semibold text-gray-700">
                {statusConfig.statusText}
              </p>
            </div>
          );
        default:
          return (
            <div className="flex flex-col justify-between w-full h-full p-4 rounded-xl">
              <CardTitle title={topic.name} />
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon
                  icon={statusConfig.icon}
                  className={`${statusConfig.iconColor} text-2xl`}
                />
                <p className="text-md font-semibold text-gray-700">
                  {statusConfig.statusText}
                </p>
              </div>
              <p className="text-xs text-gray-500">{timeText}</p>
            </div>
          );
      }
    };

    const renderPipelineContent = () => {
      const runCount = messages?.length || 0;
      const lastRun =
        runCount > 0
          ? new Date(messages[messages.length - 1].timestamp).toLocaleString()
          : null;
      const nextRunText = nextRun ? nextRun.toLocaleString() : "N/A";

      return (
        <div className="w-full bg-green-50 border border-green-200 rounded-lg p-2 shadow-sm font-sans">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <span className="text-lg font-bold tracking-tight text-gray-900 pr-8">
                {topic.name}
              </span>
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {card.type}
              </span>
              -
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {card.subtype}
              </span>
              -
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {displayVariant}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => console.log("Test cron for", card)}
                className="p-1 text-gray-900 hover:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faPlay} />
              </button>
              <button
                onClick={() => console.log("Settings cron for", card)}
                className="p-1 text-gray-900 hover:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faCog} />
              </button>
            </div>
          </div>

          <div className="flex">
            <div className="flex flex-wrap text-sm text-gray-800 mt-2 divide-x divide-gray-300 w-full">
              <div className="flex px-2">
                <span className="font-semibold text-gray-900">Next run:</span>
                <span className="font-medium ml-1">{nextRunText}</span>
              </div>
              <div className="flex px-2">
                <button
                  onClick={() => setShowCondition(!showCondition)}
                  className="text-sm font-semibold text-blue-600 hover:underline focus:outline-none"
                >
                  Cron
                </button>
              </div>
              <div className="flex px-2">
                <span className="font-semibold text-gray-900">Run:</span>
                <span className="font-medium text-green-600 ml-1">
                  {displayParameters.action}
                </span>
              </div>
            </div>

            <div className="text-xs font-medium text-gray-800 mt-2 w-full text-right">
              <div className="bg-green-100 border border-green-200 px-2 rounded-lg inline-block mr-2">
                Runs: {runCount}
              </div>
              <div className="bg-green-100 border border-green-200 px-2 rounded-lg inline-block mr-2">
                Last: {lastRun}
              </div>
            </div>
          </div>

          {showCondition && (
            <div className="mt-2">
              <pre className="bg-gray-100 rounded p-2 text-xs font-mono text-gray-800 mt-1 overflow-auto">
                {card.params.schedule}
              </pre>
            </div>
          )}
        </div>
      );
    };

    return (
      <CardConditionalHighlight card={card}>
        <div className="bg-seasalt transition-transform duration-300 w-full h-full">
          {!pipeline && renderContent()}
          {pipeline && renderPipelineContent()}
        </div>
      </CardConditionalHighlight>
    );
  }
);

function getCronStatusConfig(totalMinutes) {
  return totalMinutes >= 240
    ? { icon: faTimes, statusText: "FAILED", iconColor: "text-burnt-sienna" }
    : totalMinutes >= 30
    ? { icon: faExclamation, statusText: "DELAYED", iconColor: "text-sunset" }
    : {
        icon: faCheck,
        statusText: "SUCCESS",
        iconColor: "text-cambridge-blue",
      };
}

CronCard.variants = ["default"];
CronCard.variantParameters = {
  default: {
    schedule: {
      label: "Cron Schedule",
      type: "string",
      default: "0 0 * * *",
    },
    action: {
      label: "Action",
      type: "string",
      default: "",
    },
  },
};

export default CronCard;
